import logoUrl from '@Assets/logos/lili-logo-blue.svg';
import { useBoundStore } from '@Store/index.ts';
import { routesModel } from '@Router/router.consts.ts';
import { useLocation } from '@tanstack/react-router';
import { IRoute, RoutesMap } from '@Router/router.types.ts';
import { useMemo } from 'react';
import { DesktopSideBarItem } from '@Components/happyFlowTemplate/desktopSideBar/DesktopSideBarItem.tsx';

const PAGES_TO_HIDE: string[] = [
  RoutesMap.WELCOME,
  RoutesMap.INVITE_TEAM_MEMBER_INTRO,
  RoutesMap.INVITE_TEAM_MEMBER_FINISHED,
];

export const DesktopSideBar = () => {
  const { navigationStack } = useBoundStore((state) => state.onboardingSlice);
  const { pathname } = useLocation();

  const routesMap = useMemo(() => {
    const routes: IRoute[] = [];
    navigationStack.forEach((pageId) => {
      routes.push(routesModel[pageId]);
    });
    return routes.filter((route) => {
      if (!PAGES_TO_HIDE.includes(route.id)) {
        return route.id;
      }
    });
  }, [pathname, navigationStack]);

  const shouldShowItems = useMemo(() => {
    return routesMap.some(({ path }) => path.includes(pathname));
  }, [routesMap, pathname]);

  const reduceObjectsToNames = () => {
    return routesMap.reduce((names: string[], obj) => {
      if ('name' in obj) {
        names.push(obj.name);
      }
      return names;
    }, []);
  };

  const uniqueRouteName = useMemo(() => {
    const routeNames = reduceObjectsToNames();
    return Array.from(new Set(routeNames));
  }, [routesMap]);

  const checkIfActiveByRouteName = (routeName: string) => {
    return routesMap.some((route) => route.name === routeName && pathname.includes(route.path));
  };

  const activeRouteIndex = useMemo(() => {
    return uniqueRouteName.findIndex((routeName) => checkIfActiveByRouteName(routeName));
  }, [uniqueRouteName]);

  return (
    <div className='pt-[172px] px-[60px] min-w-[300px] w-[300px] xl:w-[400px] bg-surface-primary-bw-01 border-r border-outline-primary-bw-03 h-full hidden sm:flex'>
      <div
        className={`flex flex-col w-full gap-[4px] ${!shouldShowItems ? 'self-start pt-[100px]' : ''}`}>
        <img className='w-[52px]' src={logoUrl} alt='Lili logo' />
        {shouldShowItems && (
          <ul className='flex flex-col'>
            {uniqueRouteName.map((routeName, index) => {
              const isActive = index === activeRouteIndex;
              const isPrevious = index < activeRouteIndex;

              return (
                <DesktopSideBarItem
                  routeName={routeName}
                  isActive={isActive}
                  isPrevious={isPrevious}
                  key={routeName}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
